import React from 'react';
import { Home, Sparkles, BookOpen, TrendingUp, Library } from 'lucide-react';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows 
} from '../shared/config/theme';

const BottomNav = ({ 
  activeTab = 'home',
  onTabChange,
  sessionActive = false, 
  sessionProgress = 0 
}) => {
  const tabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'session', label: 'Session', icon: Sparkles },
    { id: 'journal', label: 'Journal', icon: BookOpen },
    { id: 'progress', label: 'Progress', icon: TrendingUp },
    { id: 'library', label: 'Library', icon: Library },
  ];

  return (
    <>
      {/* ✅ Session Progress Bar - Using theme gradient */}
      {sessionActive && (
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-peach-100">
          <div 
            className={`h-full bg-gradient-to-r ${gradients.button} transition-all duration-500`}
            style={{ width: `${sessionProgress}%` }}
          />
        </div>
      )}

      {/* ✅ Bottom Navigation - Using theme colors */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-peach-200 shadow-lg">
        <div className="relative max-w-screen-xl mx-auto px-2">
          <div className="flex justify-around items-center h-20">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className="flex flex-col items-center justify-center flex-1 h-full transition-all duration-300 relative group"
                  aria-label={tab.label}
                >
                  <div className="relative z-10">
                    {/* ✅ Icon Container - Using theme gradient when active */}
                    <div className={`relative transition-all duration-300 ${
                      active ? 'scale-110 -translate-y-1' : 'scale-100 group-hover:scale-105'
                    }`}>
                      {/* Icon Background - Orange gradient when active, transparent when inactive */}
                      <div className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        active 
                          ? `bg-gradient-to-br ${gradients.button} shadow-lg` 
                          : 'bg-transparent'
                      }`}>
                        <Icon 
                          size={22} 
                          strokeWidth={active ? 2.5 : 2}
                          className={`relative z-10 transition-colors duration-300 ${
                            active ? 'text-white' : 'text-gray-400'
                          }`}
                        />
                        
                        {/* Session Active Indicator */}
                        {tab.id === 'session' && sessionActive && (
                          <div className="absolute -top-1 -right-1 w-3 h-3">
                            <div className="absolute inset-0 bg-green-500 rounded-full animate-pulse border-2 border-white"></div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* ✅ Label - Using theme colors */}
                    <span className={`block text-[10px] mt-1.5 font-semibold transition-all duration-300 ${
                      active ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {tab.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ✅ Bottom Safe Area - Using theme background */}
        <div className="h-safe-area-inset-bottom bg-white/95"></div>
      </nav>

      <style>{`
        @supports (padding-bottom: env(safe-area-inset-bottom)) {
          .h-safe-area-inset-bottom {
            height: env(safe-area-inset-bottom);
          }
        }
      `}</style>
    </>
  );
};

export default BottomNav;